import { useState } from "react";
import { eng } from "@/lib/engineApi";
import { useGrid } from "@/state/grid";

type Scope = "table" | "station" | "site";
const SCOPES: [Scope, string][] = [
  ["table", "Tables"],
  ["station", "Stations"],
  ["site", "Site-wide"],
];

/** Manage inspection items (ITP) — add/edit/delete per scope.
 *  Faithful to the engine's ITP manager; mutates via Store.addItp/updateItp/deleteItp. */
export default function ItpManagerModal({ onClose }: { onClose: () => void }) {
  const e = eng();
  const bump = useGrid((s) => s.bump);
  const [scope, setScope] = useState<Scope>("table");
  const [editIdx, setEditIdx] = useState<number | null>(null);
  const [f, setF] = useState({ cat: "", code: "", item: "", crit: "", itemScope: "mechanical" });
  const [, setTick] = useState(0);
  const refresh = () => {
    bump();
    setTick((t) => t + 1);
  };

  const list = (): any[] =>
    scope === "table" ? e.D.tableItp : scope === "station" ? e.D.stationItp : e.D.siteItp;

  const reset = () => {
    setEditIdx(null);
    setF({ cat: "", code: "", item: "", crit: "", itemScope: "mechanical" });
  };
  const startEdit = (i: number) => {
    const it = list()[i];
    setEditIdx(i);
    setF({ cat: it.cat || "", code: it.code || "", item: it.item || "", crit: it.crit || "", itemScope: it.scope || "mechanical" });
  };
  const submit = () => {
    if (!f.item.trim() || !f.cat.trim()) {
      e.toast("Item name and category are required", "warn");
      return;
    }
    const payload: any = { cat: f.cat.trim(), code: f.code.trim(), item: f.item.trim(), crit: f.crit.trim() };
    if (scope === "table") payload.scope = f.itemScope;
    if (editIdx == null) {
      e.Store.addItp(scope, payload);
      e.toast("Inspection item added", "success");
    } else {
      e.Store.updateItp(scope, editIdx, payload);
      e.toast("Inspection item updated", "success");
    }
    e.saveToStorage();
    reset();
    refresh();
  };
  const del = (i: number) => {
    const it = list()[i];
    if (!confirm(`Delete "${it.item}"? Recorded results for this item will be re-indexed.`)) return;
    const orphaned = e.Store.deleteItp(scope, i);
    e.saveToStorage();
    if (editIdx === i) reset();
    refresh();
    e.toast(orphaned ? `Item deleted · ${orphaned} recorded result(s) re-indexed` : "Item deleted", "info");
  };

  const items = list();

  return (
    <div className="gp-modal-overlay" onClick={onClose}>
      <div className="gp-modal" onClick={(ev) => ev.stopPropagation()}>
        <div className="gp-modal-head">
          <h2>Manage inspection items</h2>
          <button className="gp-modal-x" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <div className="cl-filter" style={{ padding: "10px 16px 0" }}>
          {SCOPES.map(([s, l]) => (
            <button key={s} className={`cl-pill ${scope === s ? "sel" : ""}`} onClick={() => { setScope(s); reset(); }}>
              {l} <b>{(s === "table" ? e.D.tableItp : s === "station" ? e.D.stationItp : e.D.siteItp).length}</b>
            </button>
          ))}
        </div>

        <div className="gp-modal-body">
          <div className="itp-list">
            {items.map((it, i) => (
              <div className={`itp-row ${editIdx === i ? "sel" : ""}`} key={i}>
                <span className="itp-code mono">{it.code}-{String(i + 1).padStart(3, "0")}</span>
                <span className="itp-name">
                  {it.item}
                  <span className="itp-cat">{it.cat}{it.scope ? ` · ${it.scope}` : ""}</span>
                </span>
                <button className="obtn" style={{ fontSize: 10, padding: "3px 8px" }} onClick={() => startEdit(i)}>Edit</button>
                <button className="obtn nu" style={{ fontSize: 10, padding: "3px 8px" }} onClick={() => del(i)}>Delete</button>
              </div>
            ))}
          </div>

          <div className="itp-form">
            <div className="itp-form-h">{editIdx == null ? "Add inspection item" : `Edit item ${editIdx + 1}`}</div>
            <div className="itp-grid">
              <label>Category<input value={f.cat} onChange={(ev) => setF({ ...f, cat: ev.target.value })} placeholder="e.g. Table Mechanical Assembly" /></label>
              <label>Code<input value={f.code} onChange={(ev) => setF({ ...f, code: ev.target.value })} placeholder="MECH" style={{ width: 90 }} /></label>
              {scope === "table" && (
                <label>Scope
                  <select value={f.itemScope} onChange={(ev) => setF({ ...f, itemScope: ev.target.value })}>
                    <option value="mechanical">Mechanical</option>
                    <option value="electrical">Electrical</option>
                  </select>
                </label>
              )}
              <label className="wide">Item<input value={f.item} onChange={(ev) => setF({ ...f, item: ev.target.value })} placeholder="Inspection item name" /></label>
              <label className="wide">Acceptance criteria<textarea rows={3} value={f.crit} onChange={(ev) => setF({ ...f, crit: ev.target.value })} placeholder="Acceptance criteria…" /></label>
            </div>
            <div className="itp-form-btns">
              <button className="obtn ok" onClick={submit}>{editIdx == null ? "Add item" : "Save changes"}</button>
              {editIdx != null && <button className="obtn" onClick={reset}>Cancel edit</button>}
            </div>
          </div>
        </div>
      </div>
      <ModalStyles />
    </div>
  );
}

export function ModalStyles() {
  return (
    <style>{`
      .gp-modal-overlay{ position:fixed; inset:0; z-index:1000; display:flex; align-items:center; justify-content:center;
        background:rgba(8,12,16,.66); backdrop-filter:blur(3px); padding:24px; }
      .gp-modal{ background:var(--panel,#0E141B); border:1px solid var(--line); border-radius:12px;
        width:min(720px,96vw); max-height:88vh; display:flex; flex-direction:column; box-shadow:0 24px 60px rgba(0,0,0,.5); }
      .gp-modal-head{ display:flex; align-items:center; justify-content:space-between; padding:16px 18px; border-bottom:1px solid var(--line); }
      .gp-modal-head h2{ margin:0; font-size:16px; }
      .gp-modal-x{ background:none; border:none; color:var(--dim); font-size:16px; cursor:pointer; }
      .gp-modal-body{ padding:14px 18px 18px; overflow:auto; }
      .itp-list{ display:flex; flex-direction:column; gap:4px; max-height:38vh; overflow:auto; margin-bottom:16px; }
      .itp-row{ display:flex; align-items:center; gap:10px; padding:7px 10px; border:1px solid var(--line); border-radius:7px; background:var(--panel2,#141d27); }
      .itp-row.sel{ border-color:var(--steel); }
      .itp-code{ font-size:10px; color:var(--dim2); width:78px; flex:0 0 auto; }
      .itp-name{ flex:1; font-size:13px; display:flex; flex-direction:column; }
      .itp-cat{ font-size:10px; color:var(--dim); }
      .itp-form{ border-top:1px solid var(--line); padding-top:14px; }
      .itp-form-h{ font-size:11px; text-transform:uppercase; letter-spacing:.05em; color:var(--dim); margin-bottom:8px; }
      .itp-grid{ display:flex; flex-wrap:wrap; gap:10px; }
      .itp-grid label{ display:flex; flex-direction:column; gap:4px; font-size:11px; color:var(--dim); flex:1 1 200px; }
      .itp-grid label.wide{ flex:1 1 100%; }
      .itp-grid input, .itp-grid select, .itp-grid textarea{ padding:8px 10px; background:var(--bg2,#0b1117); color:var(--ink);
        border:1px solid var(--line); border-radius:7px; font:13px/1.3 'IBM Plex Sans',system-ui,sans-serif; }
      .itp-form-btns{ display:flex; gap:8px; margin-top:12px; }
    `}</style>
  );
}
